import { useCallback, useEffect, useRef, useState } from 'react'
import { todayISO, tomorrowISO, formatRelative, formatHuman } from '../data/workdate.js'
import { putPhoto } from '../data/photoStore.js'
import PhotoStrip from '../components/PhotoStrip.jsx'
import './owner.css'

/*
 * Owner / Superintendent — the smallest development-only surface that closes
 * the Build 001 loop.
 *
 * NEW — NOT YET FIGMA-APPROVED. There is no owner frame in the approved Figma
 * prototype, so this is built as the smallest extension of the existing visual
 * language. No approved screen was changed to accommodate it.
 *
 * DEVELOPMENT ONLY — THERE IS NO AUTHENTICATION. Flow has no identity or
 * authorization layer yet, so nothing here is protected. Accepted for fixture
 * testing; a SHIP BLOCKER recorded in the Build 001 Upgrade Register.
 *
 * Two rules this screen exists to honour:
 *   1. Independent Facts — the assigned project and the reported project are
 *      carried separately and BOTH shown where they differ. The board never
 *      rewrites an assignment to match a capture, or the reverse.
 *   2. Assignment does not gate check-in — a worker with no assignment who
 *      checked in still appears here, because that is the case the owner most
 *      needs to see.
 */
export default function Owner({ adapter, tenantId, onExit }) {
  const [employees, setEmployees] = useState([])
  const [projects, setProjects] = useState([])
  const [rows, setRows] = useState(null)

  const [boardDate, setBoardDate] = useState(todayISO())
  const [who, setWho] = useState('')
  const [what, setWhat] = useState('')
  const [when, setWhen] = useState(todayISO())
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState('')

  // BUILD 003 — work detail on the assignment being created or edited.
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [photoIds, setPhotoIds] = useState([])
  const [editingId, setEditingId] = useState(null)      // null = creating a new one
  const [assignments, setAssignments] = useState({ active: [], voided: [] })
  const [openId, setOpenId] = useState(null)            // which row is expanded
  const [showHistory, setShowHistory] = useState(false)
  const [confirmVoid, setConfirmVoid] = useState(null)
  const takeRef = useRef(null)
  const uploadRef = useRef(null)
  const formRef = useRef(null)

  useEffect(() => {
    let alive = true
    Promise.all([
      adapter.listEmployees(tenantId).catch(() => []),
      adapter.listProjects(tenantId).catch(() => []),
    ]).then(([e, p]) => {
      if (!alive) return
      setEmployees(e); setProjects(p)
      setWho((v) => v || e[0]?.id || '')
      setWhat((v) => v || p[0]?.id || '')
    })
    return () => { alive = false }
  }, [adapter, tenantId])

  const loadBoard = useCallback(() => {
    adapter.listWorkforceForDate(tenantId, boardDate)
      .then(setRows)
      .catch(() => setRows([]))
  }, [adapter, tenantId, boardDate])

  useEffect(() => { loadBoard() }, [loadBoard])

  const loadAssignments = useCallback(() => {
    if (typeof adapter.listAssignments !== 'function') return
    adapter.listAssignments(tenantId)
      .then(setAssignments)
      .catch(() => setAssignments({ active: [], voided: [] }))
  }, [adapter, tenantId])

  useEffect(() => { loadAssignments() }, [loadAssignments])

  /*
   * The worker's phone and this board are the same browser during fixture
   * testing, and a check-in from another tab writes to localStorage. Refresh
   * on focus and on the storage event so the owner sees the change without a
   * manual reload — this is a demo convenience, not a sync design.
   */
  useEffect(() => {
    const onFocus = () => loadBoard()
    window.addEventListener('focus', onFocus)
    window.addEventListener('storage', onFocus)
    return () => {
      window.removeEventListener('focus', onFocus)
      window.removeEventListener('storage', onFocus)
    }
  }, [loadBoard])

  /*
   * A picked photo goes straight into IndexedDB and only its id is held in
   * form state — the blob never enters React state or localStorage.
   */
  async function onPhotoPicked(e) {
    const files = [...(e.target.files ?? [])]
    e.target.value = ''
    if (!files.length) return
    setSaving(true)
    try {
      const ids = []
      for (const file of files) {
        ids.push(await putPhoto({ name: file.name, type: file.type, blob: file }))
      }
      setPhotoIds((p) => [...p, ...ids])
    } catch {
      setSaved('Could not attach that photo on this device.')
    }
    setSaving(false)
  }

  function removePhoto(id) {
    // Dropped from the draft only. Nothing is deleted from storage, because a
    // saved Assignment elsewhere may still reference this photo.
    setPhotoIds((p) => p.filter((x) => x !== id))
  }

  /*
   * Clears the FORM only. It deliberately does not clear `saved`: save() sets
   * the confirmation and then resets the fields, so wiping the message here
   * would erase the very thing that tells the owner the save worked.
   */
  function resetForm() {
    setEditingId(null)
    setTitle(''); setDescription(''); setPhotoIds([])
  }

  /* Load an existing Assignment into the form. Worker and date are the
     placement's identity and are locked while editing — moving someone to a
     different day is a void plus a new Assignment, which keeps the history
     honest rather than silently rewriting where they were expected. */
  function startEdit(a) {
    setEditingId(a.assignmentId)
    setWho(a.employeeId)
    setWhat(a.projectId)
    setWhen(a.workDate ?? todayISO())
    setTitle(a.title ?? '')
    setDescription(a.description ?? '')
    setPhotoIds(a.photoIds ?? [])
    setSaved('')
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  async function save(e) {
    e.preventDefault()
    if (!who || !what || !when || saving) return
    setSaving(true)
    try {
      const name = employees.find((x) => x.id === who)?.name ?? 'Worker'
      const proj = projects.find((x) => x.id === what)?.name ?? 'project'
      if (editingId) {
        // The project can move; the detail is written through the detail API,
        // so an edit can never reach a captured fact.
        await adapter.saveAssignment({ employeeId: who, projectId: what, workDate: when })
        await adapter.updateAssignmentDetail(editingId, { title, description, photoIds })
        setSaved(`Updated — ${name} → ${proj}, ${formatRelative(when)}`)
      } else {
        await adapter.saveAssignment({ employeeId: who, projectId: what, workDate: when, title, description, photoIds })
        setSaved(`Saved — ${name} → ${proj}, ${formatRelative(when)}`)
      }
      resetForm()
      loadAssignments()
      if (when === boardDate) loadBoard()
    } catch {
      setSaved('Could not save that assignment.')
    }
    setSaving(false)
  }

  /*
   * The button says Delete. The operation is VOID: the Assignment leaves both
   * active views and keeps everything it had — title, instructions, photos,
   * history. Captured Check-In / Check-Out / Workday facts are not touched at
   * all; they are a different record entirely.
   */
  async function doVoid(assignmentId) {
    setSaving(true)
    try {
      await adapter.voidAssignment(assignmentId)
      setConfirmVoid(null)
      setOpenId(null)
      if (editingId === assignmentId) resetForm()
      loadAssignments()
      loadBoard()
    } catch {
      setSaved('Could not delete that assignment.')
    }
    setSaving(false)
  }

  return (
    <div className="ow">
      <div className="ow-devbar">
        DEVELOPMENT ONLY · FIXTURE DATA · NO AUTHENTICATION · NOT FOR PRODUCTION USE
      </div>

      <div className="ow-header">
        <div>
          <h1>Workforce</h1>
          <p className="ow-header__sub">
            Owner / Superintendent · invented test data only
          </p>
        </div>
        <button type="button" className="ow-exit" onClick={onExit}>Exit to Flow</button>
      </div>

      <div className="ow-body">
        <section className="ow-section" ref={formRef}>
          <h2>{editingId ? 'EDIT ASSIGNMENT' : 'ASSIGN WORKER'}</h2>
          <div className="ow-card">
            <form className="ow-form" onSubmit={save}>
              <div className="ow-form__row">
                <div className="ow-field">
                  <label htmlFor="ow-who">WORKER</label>
                  <select id="ow-who" value={who} disabled={!!editingId}
                          onChange={(e) => setWho(e.target.value)}>
                    {employees.map((e) => (
                      <option key={e.id} value={e.id}>{e.name}</option>
                    ))}
                  </select>
                </div>
                <div className="ow-field">
                  <label htmlFor="ow-what">PROJECT</label>
                  <select id="ow-what" value={what} onChange={(e) => setWhat(e.target.value)}>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}{p.pendingApproval ? ' (pending approval)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="ow-field">
                  <label htmlFor="ow-when">WORK DATE</label>
                  <input id="ow-when" type="date" value={when} disabled={!!editingId}
                         onChange={(e) => setWhen(e.target.value)} />
                </div>
              </div>

              {editingId ? (
                <p className="ow-lockednote">
                  Worker and date are fixed while editing. To move this work to
                  another person or day, delete it and create a new assignment —
                  that keeps the history straight.
                </p>
              ) : (
                <div className="ow-quickdates">
                  <button type="button" aria-pressed={when === todayISO()}
                          onClick={() => setWhen(todayISO())}>Today</button>
                  <button type="button" aria-pressed={when === tomorrowISO()}
                          onClick={() => setWhen(tomorrowISO())}>Tomorrow</button>
                </div>
              )}

              {/* BUILD 003 — the work detail. NEW / NOT YET FIGMA-APPROVED,
                  built in the existing Owner visual language. */}
              <div className="ow-field">
                <label htmlFor="ow-title">ASSIGNMENT TITLE</label>
                <input id="ow-title" type="text" value={title} maxLength={120}
                       placeholder="e.g. Frame the north wall"
                       onChange={(e) => setTitle(e.target.value)} />
              </div>

              <div className="ow-field">
                <label htmlFor="ow-desc">INSTRUCTIONS</label>
                <textarea id="ow-desc" rows={4} value={description}
                          placeholder="What needs doing, and anything the crew should know before they start."
                          onChange={(e) => setDescription(e.target.value)} />
              </div>

              <div className="ow-field">
                <label>PHOTOS</label>
                <div className="ow-photobtns">
                  <button type="button" className="ow-photobtn" disabled={saving}
                          onClick={() => takeRef.current?.click()}>Take Photo</button>
                  <button type="button" className="ow-photobtn" disabled={saving}
                          onClick={() => uploadRef.current?.click()}>Upload Photo</button>
                </div>
                <PhotoStrip photoIds={photoIds} onRemove={removePhoto}
                            emptyLabel="No photos attached." />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                <button type="submit" className="ow-save" disabled={saving || !who || !what || !when}>
                  {saving ? 'SAVING…' : editingId ? 'SAVE CHANGES' : 'SAVE ASSIGNMENT'}
                </button>
                {editingId && (
                  <button type="button" className="ow-exit" disabled={saving}
                          onClick={() => { resetForm(); setSaved('') }}>
                    Cancel edit
                  </button>
                )}
                {saved && <span className="ow-saved" role="status">Saved — {saved}</span>}
              </div>

              {/* Take Photo uses the device camera; Upload Photo picks an
                  existing one. Same mechanism the Daily Report already uses. */}
              <input ref={takeRef} type="file" accept="image/*" capture="environment"
                     style={{ display: 'none' }} onChange={onPhotoPicked} />
              <input ref={uploadRef} type="file" accept="image/*" multiple
                     style={{ display: 'none' }} onChange={onPhotoPicked} />
            </form>
          </div>
        </section>

        <section className="ow-section">
          <div className="ow-boardhead">
            <h2 style={{ marginBottom: 0 }}>WORKFORCE {boardDate === todayISO() ? 'TODAY' : formatRelative(boardDate).toUpperCase()}</h2>
            <div className="ow-quickdates">
              <button type="button" aria-pressed={boardDate === todayISO()}
                      onClick={() => setBoardDate(todayISO())}>Today</button>
              <button type="button" aria-pressed={boardDate === tomorrowISO()}
                      onClick={() => setBoardDate(tomorrowISO())}>Tomorrow</button>
              <input type="date" value={boardDate} aria-label="Board date"
                     onChange={(e) => setBoardDate(e.target.value)}
                     style={{ fontFamily: 'inherit', fontSize: 13, padding: '6px 10px',
                              border: '1px solid var(--ow-border)', borderRadius: 999 }} />
              <button type="button" onClick={loadBoard}>Refresh</button>
            </div>
          </div>

          <div className="ow-card" style={{ background: '#fff' }}>
            <div className="ow-table-wrap">
              <table className="ow-table">
                <thead>
                  <tr>
                    <th>Worker</th>
                    <th>Assigned project</th>
                    <th>Reported project</th>
                    <th>Status</th>
                    <th>Check in</th>
                    <th>Check out</th>
                    <th>Hours</th>
                  </tr>
                </thead>
                <tbody>
                  {rows === null ? (
                    <tr><td colSpan={7} className="ow-empty">Loading…</td></tr>
                  ) : rows.length === 0 ? (
                    <tr><td colSpan={7} className="ow-empty">
                      Nobody assigned and nobody checked in for {formatRelative(boardDate)}.
                    </td></tr>
                  ) : rows.map((r) => {
                    // Both facts, never merged. A difference is surfaced, not resolved.
                    const differs = !r.unmatched && !!r.assignedProjectName && !!r.reportedProjectName
                      && r.assignedProjectName !== r.reportedProjectName
                    return (
                      <tr key={r.employeeId ?? `unmatched:${r.employeeName}`}
                          className={r.unmatched ? 'ow-row--unmatched' : undefined}>
                        <td className="ow-name">
                          {/* The name EXACTLY as the person typed it. */}
                          {r.employeeName}
                          {r.unmatched && <span className="ow-typed">TYPED NAME — NOT ON THE ROSTER</span>}
                        </td>
                        <td>
                          {r.unmatched
                            ? <span className="ow-dim">No assignment &mdash; identity unresolved</span>
                            : (r.assignedProjectName ?? <span className="ow-dim">No assignment</span>)}
                        </td>
                        <td>
                          {r.reportedProjectNames?.length
                            ? r.reportedProjectNames.join(' · ')
                            : <span className="ow-dim">&mdash;</span>}
                          {differs && <span className="ow-mismatch">DIFFERENT FROM ASSIGNED</span>}
                          {r.held && <span className="ow-held">HELD — NEEDS MATCHING</span>}
                        </td>
                        <td>
                          {/*
                            U-4: an unmatched capture leads with what the owner
                            has to act on — somebody is here and Flow does not
                            know who. The workday state still shows underneath,
                            because whether they are currently on the clock
                            matters too.
                          */}
                          {r.unmatched ? (
                            <>
                              <span className="ow-pill ow-pill--unmatched">Unmatched &mdash; needs review</span>
                              <span className="ow-list__meta">{stateLabel(r.status)}</span>
                            </>
                          ) : (
                            <StatusPill status={r.status} />
                          )}
                          {/* A past day Flow never saw end. Payroll validation
                              stays blocked until a reviewer resolves it. */}
                          {r.needsReview && (
                            <span className="ow-mismatch">REVIEW REQUIRED</span>
                          )}
                          {r.employeeReport && (
                            <span className="ow-list__meta">
                              Worker reported: &ldquo;{r.employeeReport.text}&rdquo;
                            </span>
                          )}
                        </td>
                        <td>{timeLabel(r.checkedInAt)}</td>
                        <td>{timeLabel(r.checkedOutAt)}</td>
                        <td>{hoursLabel(r)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* BUILD 003 — Assignments. NEW / NOT YET FIGMA-APPROVED. */}
        <section className="ow-section">
          <div className="ow-boardhead">
            <h2 style={{ marginBottom: 0 }}>ASSIGNMENTS</h2>
            <div className="ow-quickdates">
              <button type="button" onClick={loadAssignments}>Refresh</button>
            </div>
          </div>

          <div className="ow-card" style={{ background: '#fff' }}>
            {assignments.active.length === 0 ? (
              <p className="ow-empty">No active assignments yet.</p>
            ) : (
              <ul className="ow-alist">
                {assignments.active.map((a) => (
                  <li key={a.assignmentId} className="ow-arow">
                    <button type="button" className="ow-arow__head"
                            aria-expanded={openId === a.assignmentId}
                            onClick={() => setOpenId(openId === a.assignmentId ? null : a.assignmentId)}>
                      <span className="ow-arow__main">
                        <span className="ow-arow__title">
                          {a.title || <span className="ow-dim">Untitled assignment</span>}
                        </span>
                        <span className="ow-arow__meta">
                          {a.employeeName} · {a.projectName} · {a.workDate ? formatHuman(a.workDate) : 'Standing'}
                        </span>
                      </span>
                      {a.photoIds.length > 0 && (
                        <span className="ow-arow__count">{a.photoIds.length} photo{a.photoIds.length === 1 ? '' : 's'}</span>
                      )}
                      <span className="ow-arow__chev">{openId === a.assignmentId ? '\u2212' : '+'}</span>
                    </button>

                    {openId === a.assignmentId && (
                      <div className="ow-arow__body">
                        <dl className="ow-adetail">
                          <div><dt>Worker</dt><dd>{a.employeeName}</dd></div>
                          <div><dt>Project</dt><dd>{a.projectName}{a.projectLocation ? ` — ${a.projectLocation}` : ''}</dd></div>
                          <div><dt>Date</dt><dd>{a.workDate ? formatHuman(a.workDate) : 'Standing (no date)'}</dd></div>
                        </dl>

                        <p className="ow-alabel">INSTRUCTIONS</p>
                        {a.description
                          ? <p className="ow-atext">{a.description}</p>
                          : <p className="ow-atext ow-dim">No instructions given.</p>}

                        <p className="ow-alabel">PHOTOS</p>
                        <PhotoStrip photoIds={a.photoIds} emptyLabel="No photos attached." />

                        <div className="ow-aactions">
                          <button type="button" className="ow-exit" disabled={saving}
                                  onClick={() => startEdit(a)}>Edit</button>
                          {confirmVoid === a.assignmentId ? (
                            <>
                              <span className="ow-confirm">
                                Delete this assignment? It stays in history and no
                                recorded attendance changes.
                              </span>
                              <button type="button" className="ow-danger" disabled={saving}
                                      onClick={() => doVoid(a.assignmentId)}>Yes, delete</button>
                              <button type="button" className="ow-exit" disabled={saving}
                                      onClick={() => setConfirmVoid(null)}>Cancel</button>
                            </>
                          ) : (
                            <button type="button" className="ow-danger" disabled={saving}
                                    onClick={() => setConfirmVoid(a.assignmentId)}>Delete</button>
                          )}
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/*
            Voided assignments are kept and shown, but well away from active
            work and plainly labelled — history must never read as something a
            crew is expected to do.
          */}
          {assignments.voided.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <button type="button" className="ow-exit" aria-expanded={showHistory}
                      onClick={() => setShowHistory((v) => !v)}>
                {showHistory ? 'Hide' : 'Show'} deleted assignments ({assignments.voided.length})
              </button>
              {showHistory && (
                <div className="ow-card ow-card--history" style={{ marginTop: 10 }}>
                  <p className="ow-histnote">
                    Deleted assignments are voided, never erased. They keep their
                    content and history, and no recorded attendance was changed.
                  </p>
                  <ul className="ow-alist">
                    {assignments.voided.map((a) => (
                      <li key={a.assignmentId} className="ow-arow ow-arow--voided">
                        <div className="ow-arow__head ow-arow__head--static">
                          <span className="ow-arow__main">
                            <span className="ow-arow__title">
                              {a.title || <span className="ow-dim">Untitled assignment</span>}
                              <span className="ow-voidtag">DELETED</span>
                            </span>
                            <span className="ow-arow__meta">
                              {a.employeeName} · {a.projectName} · {a.workDate ? formatHuman(a.workDate) : 'Standing'}
                              {a.voidedAt ? ` · deleted ${new Date(a.voidedAt).toLocaleString()}` : ''}
                            </span>
                          </span>
                        </div>
                        {a.description && <p className="ow-atext ow-dim">{a.description}</p>}
                        <PhotoStrip photoIds={a.photoIds} />
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </section>

        <section className="ow-section">
          <h2>TEST DATA</h2>
          <div className="ow-card">
            <div className="ow-lists">
              <div>
                <p style={{ fontWeight: 700, fontSize: 12, color: 'var(--ow-muted)', marginBottom: 8 }}>
                  WORKERS ({employees.length})
                </p>
                <ul className="ow-list">
                  {employees.map((e) => (
                    <li key={e.id}>{e.name}<span className="ow-list__meta">{e.role}</span></li>
                  ))}
                </ul>
              </div>
              <div>
                <p style={{ fontWeight: 700, fontSize: 12, color: 'var(--ow-muted)', marginBottom: 8 }}>
                  PROJECTS ({projects.length})
                </p>
                <ul className="ow-list">
                  {projects.map((p) => (
                    <li key={p.id}>
                      {p.name}
                      <span className="ow-list__meta">
                        {p.location || 'No location'}{p.pendingApproval ? ' · pending approval' : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}

/* Plain-language workday state, shown under an unmatched row's pill. */
function stateLabel(status) {
  if (status === 'working') return 'Currently checked in'
  if (status === 'checked-out') return 'Checked out'
  return 'No workday recorded'
}

function StatusPill({ status }) {
  if (status === 'working') return <span className="ow-pill ow-pill--working">WORKING</span>
  if (status === 'checked-out') return <span className="ow-pill ow-pill--out">CHECKED OUT</span>
  // A past day with a check-in and no check-out — never shown as WORKING.
  if (status === 'review') return <span className="ow-pill ow-pill--notin">NO CHECK-OUT</span>
  return <span className="ow-pill ow-pill--notin">NOT IN</span>
}

function timeLabel(iso) {
  if (!iso) return <span className="ow-dim">&mdash;</span>
  try { return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) }
  catch { return <span className="ow-dim">&mdash;</span> }
}

/*
 * Hours for Build 001 = check-out minus check-in for the day, summed across
 * every completed segment. A still-open workday shows no total, because the
 * day is not finished — a running total would be an inference, not a fact.
 */
function hoursLabel(r) {
  if (r.status === 'working') return <span className="ow-dim">in progress</span>
  // Deliberately NOT a number. The day has no end, so any total would be
  // invented — and payroll validation is blocked until a reviewer resolves it.
  if (r.status === 'review') return <span className="ow-dim">unresolved</span>
  // Only a day with no completed segment has nothing to total. A day that IS
  // checked out always shows its computed time, including 0m — an em-dash
  // there would read as "no data" when the real answer is "no time".
  if (r.status !== 'checked-out') return <span className="ow-dim">&mdash;</span>
  const h = Math.floor(r.minutes / 60)
  const m = r.minutes % 60
  return h ? `${h}h ${m}m` : `${m}m`
}
